import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  BugReport as BugIcon,
  ContactSupport as SupportIcon,
  Info as InfoIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import useBDocTranslator from "../../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  errorContainer: {
    margin: theme.spacing(2, 0),
    border: `2px solid ${theme.palette.error.main}`,
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  textCenter: {
    textAlign: "center",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  errorDetails: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: "monospace",
    fontSize: "0.85rem",
    overflow: "auto",
    maxHeight: 200,
  },
  suggestion: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
  },
}));

/**
 * Error Boundary específico para componentes dentales
 * Maneja errores relacionados con permisos, backend, y componentes dental
 */
class DentalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el state para mostrar la UI de error
    const errorId = `dental_error_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Capturar información adicional del error
    this.setState({
      errorInfo,
    });

    // Log del error para debugging
    console.group("🦷 Dental Error Boundary - Error Captured");
    console.error("Error:", error);
    console.error("Error Info:", errorInfo);
    console.error("Props:", this.props);
    console.groupEnd();

    // Enviar error al servicio de logging si está configurado
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    const errorData = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      module: "dental",
      component: this.props.componentName || "unknown",
      patientId: this.props.patientId || null,
      userId: this.props.userId || null,
    };

    // Aquí se puede integrar con servicios como Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === "development") {
      console.log("Error logged:", errorData);
    }

    // Opcional: Enviar al backend para logging
    if (this.props.onError) {
      this.props.onError(errorData);
    }
  };

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));

    // Ejecutar callback de retry si está definido
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  getErrorType = (error) => {
    if (!error) return "unknown";

    const message = error.message.toLowerCase();

    if (
      message.includes("permission") ||
      message.includes("unauthorized") ||
      message.includes("access denied")
    ) {
      return "permission";
    }
    if (
      message.includes("network") ||
      message.includes("fetch") ||
      message.includes("timeout")
    ) {
      return "network";
    }
    if (
      message.includes("dental") ||
      message.includes("tooth") ||
      message.includes("chart")
    ) {
      return "dental_specific";
    }
    if (message.includes("validation") || message.includes("invalid")) {
      return "validation";
    }

    return "general";
  };

  getErrorSuggestions = (errorType) => {
    const { t } = this.props;

    switch (errorType) {
      case "permission":
        return [
          t("Verifique que tiene los permisos necesarios para esta acción"),
          t("Contacte al administrador si necesita acceso adicional"),
          t("Asegúrese de que su sesión no haya expirado"),
        ];
      case "network":
        return [
          t("Verifique su conexión a internet"),
          t("Intente recargar la página"),
          t("El servidor puede estar temporalmente no disponible"),
        ];
      case "dental_specific":
        return [
          t("Verifique que el paciente existe y tiene odontogramas"),
          t("Asegúrese de que los datos dentales estén en formato correcto"),
          t("Contacte al equipo técnico si el problema persiste"),
        ];
      case "validation":
        return [
          t("Revise que todos los campos requeridos estén completos"),
          t("Verifique el formato de los datos ingresados"),
          t("Consulte la documentación para valores válidos"),
        ];
      default:
        return [
          t("Intente recargar la página"),
          t("Verifique que todos los campos estén correctos"),
          t("Contacte al soporte técnico si el problema persiste"),
        ];
    }
  };

  render() {
    if (this.state.hasError) {
      const { t, showDetails = false, allowRetry = true } = this.props;
      const errorType = this.getErrorType(this.state.error);
      const suggestions = this.getErrorSuggestions(errorType);

      return (
        <DentalErrorBoundaryContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          errorType={errorType}
          suggestions={suggestions}
          retryCount={this.state.retryCount}
          onRetry={allowRetry ? this.handleRetry : null}
          showDetails={showDetails}
          t={t}
        />
      );
    }

    return this.props.children;
  }
}

// Componente funcional para renderizar la UI de error
function DentalErrorBoundaryContent({
  error,
  errorInfo,
  errorId,
  errorType,
  suggestions,
  retryCount,
  onRetry,
  showDetails,
  t,
}) {
  const classes = useStyles();

  const getErrorTitle = () => {
    switch (errorType) {
      case "permission":
        return t("Error de Permisos");
      case "network":
        return t("Error de Conexión");
      case "dental_specific":
        return t("Error en Módulo Dental");
      case "validation":
        return t("Error de Validación");
      default:
        return t("Error Inesperado");
    }
  };

  const getErrorMessage = () => {
    switch (errorType) {
      case "permission":
        return t("No tiene los permisos necesarios para realizar esta acción.");
      case "network":
        return t("No se pudo conectar con el servidor. Verifique su conexión.");
      case "dental_specific":
        return t("Ocurrió un error específico del módulo dental.");
      case "validation":
        return t("Los datos ingresados no son válidos.");
      default:
        return t("Ha ocurrido un error inesperado en la aplicación.");
    }
  };

  return (
    <Card className={classes.errorContainer}>
      <CardContent>
        <div className={classes.textCenter}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="h5" gutterBottom>
            {getErrorTitle()}
          </Typography>
        </div>

        <Alert severity="error" style={{ marginBottom: 16 }}>
          {getErrorMessage()}
        </Alert>

        {suggestions.length > 0 && (
          <Box className={classes.suggestion}>
            <Typography variant="subtitle2" gutterBottom>
              <InfoIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
              {t("Sugerencias para resolver el problema")}:
            </Typography>
            <List dense>
              {suggestions.map((suggestion, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText primary={suggestion} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {showDetails && (
          <Accordion style={{ marginTop: 16 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                <BugIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
                {t("Detalles Técnicos")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box style={{ width: "100%" }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t("ID del Error")}:</strong> {errorId}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>{t("Tipo")}:</strong> {errorType}
                </Typography>
                {retryCount > 0 && (
                  <Typography variant="body2" gutterBottom>
                    <strong>{t("Intentos de Reintento")}:</strong> {retryCount}
                  </Typography>
                )}
                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="body2" gutterBottom>
                  <strong>{t("Mensaje de Error")}:</strong>
                </Typography>
                <div className={classes.errorDetails}>
                  {error?.message || t("No hay mensaje de error disponible")}
                </div>
                {errorInfo && (
                  <>
                    <Typography
                      variant="body2"
                      gutterBottom
                      style={{ marginTop: 16 }}
                    >
                      <strong>{t("Stack Trace del Componente")}:</strong>
                    </Typography>
                    <div className={classes.errorDetails}>
                      {errorInfo.componentStack}
                    </div>
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        <div className={classes.actionButtons}>
          {onRetry && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              {t("Reintentar")}
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SupportIcon />}
            onClick={() => {
              // Copiar información del error al clipboard
              const errorData = {
                errorId,
                message: error?.message,
                type: errorType,
                timestamp: new Date().toISOString(),
              };
              navigator.clipboard?.writeText(
                JSON.stringify(errorData, null, 2)
              );
            }}
          >
            {t("Reportar Error")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// HOC para aplicar automáticamente el error boundary
export function withDentalErrorBoundary(WrappedComponent, boundaryProps = {}) {
  function ErrorBoundaryWrappedComponent(props) {
    const { t } = useBDocTranslator();

    return (
      <DentalErrorBoundary {...boundaryProps} t={t}>
        <WrappedComponent {...props} />
      </DentalErrorBoundary>
    );
  }

  ErrorBoundaryWrappedComponent.displayName = `withDentalErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return ErrorBoundaryWrappedComponent;
}

export default DentalErrorBoundary;
